
"use client"
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom"
import { UserOutlined, DownloadOutlined, CloseOutlined } from '@ant-design/icons';
import { Typography, Row, Col, Card, Button, Carousel, AutoComplete, Input, Flex, Tabs, notification, Spin, Space, Result, Tag } from "antd"
import { motion } from "framer-motion"
import { apiRequest } from "../utils/apiRequest";
import { useCurrency } from "../contexts/CurrencyContext";
import {
  MobileOutlined,
  WifiOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  SearchOutlined, 
  ToolOutlined, 
  RocketOutlined, 
  ReloadOutlined,
  PhoneOutlined,
  MailOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons"
import "../styles/product-cards.css"
import "./HomePage.css"
import styles from "./bubble.module.css";
import ErrorComponent from "../components/ErrorComponent"
import { useData } from "../contexts/DataContext";
import Seo, { siteBase } from "../components/Seo";
import { buildFaqJsonLd, slugifyCategory } from "../lib/seo";

import OrderDetailModal from "../components/OrderDetailModal";

const { Title, Text } = Typography

import mastercard from "../assets/mastercard.png"
import onev from "../assets/1v.png"
import applepay from "../assets/applepay.png"
import ott from "../assets/ott.png"
import paypal from "../assets/paypal.png"
import visa from "../assets/visa.png"
import ozow from "../assets/ozow.png"
import useDeviceDetection from "../hooks/useDeviceDetection";
import MobileQuickActions from "../components/MobileQuickActions"

const Itle = props => (
  <Flex align="center" justify="space-between">
    {props.title}
    <Link to="/products">
      More Products
    </Link>
  </Flex>
);

const HomePage = () => {
  const navigate = useNavigate()
  const { formatMoney, formatRange } = useCurrency()

  const formatPrice = (price) => {
    if (Array.isArray(price)) {
      return formatRange(price)
    }
    if (price === 0) {
      return "Variable Price"
    }
    return formatMoney(price)
  }

  const { providers: rawProviders, popularCategories: rawCategories, loading: dataLoading, error: dataError, fetchData } = useData();
  const [loading, setLoading] = useState(true);
  const [tracking, setTracking] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('products');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [orderData, setOrderData] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [isValidOrderNumber, setIsValidOrderNumber] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  // 'ios' | 'android' | 'desktop' — which install instructions to show. Real OS
  // detection, not "does the browser support a prompt event" (see below).
  const [installPlatform, setInstallPlatform] = useState('desktop');
  // Whether we actually have a native install prompt to trigger (Chrome/Edge on
  // Android and desktop). iOS never has one — Apple doesn't expose the API —
  // and Firefox rarely does either, so those get instructions instead of a button.
  const [canPromptInstall, setCanPromptInstall] = useState(false);
  const deferredInstallPrompt = useRef(null);
  const [trackingResult, setTrackingResult] = useState(null);

  const statusColors = { pending: 'orange', processing: 'blue', delivered: 'green', complete: 'green', cancelled: 'red', failed: 'red' };

  const isMobile = useDeviceDetection();

  // Transform raw context data into display format
  const providers = useMemo(() => (rawProviders || []).map(provider => ({
    id: provider.id,
    title: provider.name,
    icon: provider.logo_url || '/default-provider.png',
    path: `/${provider.name}`,
    color: 'orange',
  })), [rawProviders]);

  const popularCategories = useMemo(() => (rawCategories || []).map(category => ({
    id: category.id,
    title: category.name,
    range: (category.min_price != null && category.max_price != null)
      ? formatRange([category.min_price, category.max_price])
      : 'Variable',
    icon: category.logo_url,
    path: `/${category.name}`,
    color: 'orange',
  })), [rawCategories, formatRange]);

  const [filteredOptions, setFilteredOptions] = useState([]);
  const searchAbortRef = useRef(null);

  const renderSearchItem = useCallback((item) => ({
    value: item.name,
    // Category-as-product entries live at their canonical /buy/<slug> path.
    label: (
      <Link to={item.isconv ? `/buy/${slugifyCategory(item.name)}` : `/product/${item.id}`}>
        <Flex align="center" justify="space-between" style={{ padding: '4px 0', gap: 8 }}>
          <Flex align="center" gap={10}>
            {item.logo && (
              <img src={item.logo} height="36px"
                style={{ borderRadius: 6, objectFit: 'contain', background: '#f5f5f5', padding: 2 }} alt="" />
            )}
            <div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: '#1f2937', lineHeight: 1.3 }}>{item.name}</div>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>{item.provider_name}</div>
            </div>
          </Flex>
          <Text strong style={{ color: '#16a34a', fontSize: '13px', whiteSpace: 'nowrap' }}>
            {item.isconv ? 'Variable' : formatPrice(parseFloat(item.price) || 0)}
          </Text>
        </Flex>
      </Link>
    ),
  }), []);

  // Load popular products for initial dropdown state
  useEffect(() => {
    apiRequest({ method: 'get', url: '/popular_products?limit=20', suppressNotifications: true })
      .then(res => {
        const items = (res.data || []).map(p => ({ ...p, isconv: false }));
        setFilteredOptions([{
          label: <Itle title="Popular Products" />,
          options: items.map(renderSearchItem)
        }]);
      })
      .catch(() => {});
  }, [renderSearchItem]);

  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);

    if (searchAbortRef.current) searchAbortRef.current.abort();

    if (!value || value.trim().length < 2) {
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);

    const abortController = new AbortController();
    searchAbortRef.current = abortController;

    const timerId = setTimeout(async () => {
      try {
        const res = await apiRequest({
          method: 'get',
          url: `/products/search?q=${encodeURIComponent(value.trim())}&limit=30`,
          signal: abortController.signal,
          suppressNotifications: true
        });

        const items = res.data?.data || [];
        if (items.length === 0) {
          setFilteredOptions([{ label: <Itle title="No Results" />, options: [] }]);
        } else {
          // Group by provider
          const grouped = {};
          items.forEach(item => {
            const key = item.provider_name || 'Other';
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(item);
          });
          setFilteredOptions(Object.entries(grouped).map(([provider, group]) => ({
            label: <span style={{ fontWeight: 600, fontSize: 12, color: '#6b7280' }}>{provider}</span>,
            options: group.map(renderSearchItem)
          })));
        }
      } catch (err) {
        if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
          console.error('Search error:', err);
        }
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timerId);
  }, [renderSearchItem]);

  // Validate order number format: ORD-YYYYMMDD-XXXX
  const validateOrderNumber = (orderNumber) => {
    const orderRegex = /^ORD-\d{8}-\d{4}$/;
    return orderRegex.test(orderNumber);
  };

  const handleTrackingNumberChange = (e) => {
    const value = e.target.value;
    setTrackingNumber(value);
    setIsValidOrderNumber(validateOrderNumber(value));
    setTrackingResult(null);
  };

  const handleTrackOrder = async () => {
    // Validate format before making API call
    if (!isValidOrderNumber) {
      notification.error({
        message: "Invalid Order Format",
        description: "Please enter a valid order number in format: ORD-YYYYMMDD-XXXX",
        duration: 4.5,
        placement: 'topRight',
      });
      return;
    }

    try {
      setTracking(true);
      const response = await apiRequest({
        method: "get",
        url: `/trackorder?order=${trackingNumber}`,
      });

      if (response.data) {
        setTrackingResult(response.data);
        setOrderData(response.data);
      } else {
        notification.error({
          message: "Order not found",
          description: "We could not find that order, please verify and try again",
          duration: 4.5,
          placement: 'topRight',
        });
      }
    } catch (ex) {
      console.error('Tracking error:', ex);
      notification.error({
        message: "Tracking Error",
        description: "Failed to track order. Please try again.",
        duration: 4.5,
        placement: 'topRight',
      });
    } finally {
      setTracking(false);
    }
  };

  const handleInstallClick = async () => {
    // This used to check `'deferredPrompt' in window`, but nothing anywhere
    // ever set that — the button silently did nothing. The real event is
    // captured below into deferredInstallPrompt and used directly.
    const promptEvent = deferredInstallPrompt.current;
    if (!promptEvent) return;

    promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    deferredInstallPrompt.current = null;
    setCanPromptInstall(false);
    if (outcome === 'accepted') {
      setShowInstallPrompt(false);
    }
  };

  const handleDismissInstall = () => {
    setShowInstallPrompt(false);
    try {
      localStorage.setItem('pwa_install_dismissed', '1');
    } catch {
      // Private browsing can throw on write; dismissing for this visit is fine.
    }
  };

  // Fetch data from context (only re-fetches if stale >5 min)
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sync loading/error from DataContext
  useEffect(() => {
    setLoading(dataLoading);
    if (dataError) setError(dataError);
  }, [dataLoading, dataError]);

  useEffect(() => {
    // Previously this only checked whether the browser SUPPORTS a prompt event
    // ('onbeforeinstallprompt' in window) — true for Chromium browsers whether
    // or not the user is on Android/desktop/iOS. Safari (all iOS browsers are
    // WebKit under the hood) never has that property, so iOS users never got
    // an install prompt at all, while desktop Chrome users got the full
    // "show everything" card. Detect the actual platform instead.
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua)
      // iPadOS 13+ reports as "MacIntel" with a touchscreen — the one case
      // desktop Safari doesn't have.
      || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/.test(ua);
    const platform = isIOS ? 'ios' : isAndroid ? 'android' : 'desktop';
    setInstallPlatform(platform);

    const isPWAInstalled = window.matchMedia('(display-mode: standalone)').matches
      // iOS Safari doesn't support the standalone media query the same way;
      // this is its own equivalent flag.
      || window.navigator.standalone === true;

    let dismissed = false;
    try {
      dismissed = localStorage.getItem('pwa_install_dismissed') === '1';
    } catch {
      // Inaccessible storage (private mode) — treat as not dismissed.
    }

    // iOS has no programmatic install API, so it gets instructions rather than
    // a button, and can be decided immediately. Android/desktop wait for the
    // real event below before deciding whether they have anything to offer.
    if (isPWAInstalled || dismissed) {
      setShowInstallPrompt(false);
    } else if (platform === 'ios') {
      setShowInstallPrompt(true);
    }

    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      deferredInstallPrompt.current = event;
      setCanPromptInstall(true);
      if (!isPWAInstalled && !dismissed) setShowInstallPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    const onAppInstalled = () => {
      setShowInstallPrompt(false);
      deferredInstallPrompt.current = null;
    };
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  // Pre-populate track order from last successful order
  useEffect(() => {
    const lastOrderNo = localStorage.getItem("last_order_no");
    if (lastOrderNo) {
      setTrackingNumber(lastOrderNo);
      setIsValidOrderNumber(validateOrderNumber(lastOrderNo));
    }
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } },
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="logo-container">
          <img src="/logo.png" alt="Tanaka Life" className="loading-logo" style={{maxWidth: '150px'}} />
          <Spin size="medium" />
         
        </div>
         
      </div>
    );
  }

  if (error) {
    return (
     <ErrorComponent/>
    );
  }

  return (
    <div className="home-page">
      <Seo
        title="Instant Airtime, Data Bundles & ZESA Tokens for Zimbabwe"
        description="Buy instant airtime, data bundles and ZESA electricity tokens for Zimbabwe — Econet, NetOne, Telecel and TelOne. Fast, secure top-ups from South Africa and the diaspora, paid by voucher, card or instant EFT."
        canonical={`${siteBase}/`}
        keywords="airtime Zimbabwe, buy airtime Zimbabwe, data bundles Zimbabwe, Econet airtime, NetOne airtime, ZESA tokens, send airtime to Zimbabwe from South Africa, diaspora payments"
        jsonLd={buildFaqJsonLd([
          {
            question: "How do I buy airtime for Zimbabwe?",
            answer:
              "Choose your network (Econet, NetOne, Telecel or TelOne), enter the recipient's number, pay securely, and the airtime is delivered instantly.",
          },
          {
            question: "Can I buy ZESA tokens online from South Africa?",
            answer:
              "Yes. Select ZESA, enter the meter number and amount, pay, and the prepaid electricity token is delivered instantly.",
          },
          {
            question: "What payment methods can I use?",
            answer:
              "You can pay by voucher, Mastercard or Visa card, and instant EFT. Payments are processed securely.",
          },
          {
            question: "How fast is delivery?",
            answer: "Airtime, data bundles and ZESA tokens are delivered instantly after a successful payment.",
          },
        ])}
      />
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        {/* Phones and tablets open on actions, not on marketing. Rendered
            unconditionally and shown/hidden in CSS at 992px — a JS breakpoint
            would paint the desktop hero for a frame before swapping, and would
            diverge from the prerendered snapshot crawlers receive. */}
        <MobileQuickActions />

        <section className="hero-section">
          <div className="background">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
        
          <div className="hero-content">  
            <motion.div style={{zIndex: 99}} variants={itemVariants}>
              <Title level={1}>
                {"Convenient, Instant Recharge & Secure Payments".split("").map((child, idx) => (
                  <span key={idx}>
                    {child}
                  </span>
                ))}
              </Title>
              <Text className="hero-subtitle">For Zimbabweans in South Africa and the Diaspora</Text>
            
            </motion.div>

            <motion.div variants={itemVariants} className="payment-methods">
              <div className="payment-icons">
                <img src={mastercard} alt="Mastercard" />
                <img src={visa} alt="Visa" />
                <img src={ozow} alt="Ozow" />
                <img src={ott} alt="Ott" />
                <img src={onev} alt="1Voucher" />
                <img src={applepay} alt="Apple Pay" />
              </div>

              <div className="search-track-card">
                <Tabs
                  activeKey={activeTab}
                  onChange={setActiveTab}
                  centered
                  items={[
                    {
                      label: (
                        <span style={{ display: 'flex', alignItems: 'center'}}>
                          <SearchOutlined style={{ marginRight: 8 }} />
                          Search Products
                        </span>
                      ),
                      key: 'products',
                      children: (
                        <div style={{backgroundColor:'transparent'}}>
                          <AutoComplete
                            popupClassName="certain-category-search-dropdown"
                            popupMatchSelectWidth={false}
                            className="search-autocomplete"
                            options={filteredOptions}
                            onSearch={handleSearchChange}
                            notFoundContent={searchLoading ? <Spin size="small" /> : "No products found"}
                          >
                            <Input.Search
                              size="medium"
                              placeholder="What are you looking for today?"
                              allowClear
                            />
                          </AutoComplete>
                        </div>
                      )
                    },
                    {
                      label: (
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          <ClockCircleOutlined style={{ marginRight: 8 }} />
                          Track Order
                        </span>
                      ),
                      key: 'tracking',
                      children: (
                        <div style={{backgroundColor:'transparent'}}>
                          <Input.Search
                            size="medium"
                            className="search-autocomplete"
                            placeholder="Enter order number to track (e.g. ORD-20250315-0001)"
                            value={trackingNumber}
                            loading={tracking}
                            onChange={handleTrackingNumberChange}
                            onSearch={handleTrackOrder}
                            enterButton="Track"
                          />

                          {trackingNumber.length > 0 && (
                            <div style={{ marginTop: 6, fontSize: '11px', display: 'flex', alignItems: 'center', gap: 4 }}>
                              {isValidOrderNumber
                                ? <><CheckCircleOutlined style={{ color: '#16a34a' }} /> <span style={{ color: '#16a34a' }}>Valid order number</span></>
                                : <span style={{ color: '#b45309' }}>Format: ORD-YYYYMMDD-XXXX &nbsp;e.g. ORD-20250315-0001</span>
                              }
                            </div>
                          )}

                          {trackingResult && (
                            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                              <div className="track-result-card">
                                <div className="track-result-header">
                                  <div>
                                    <Text strong style={{ fontSize: 15 }}>Order #{trackingResult.order_number}</Text>
                                    <Tag color={statusColors[trackingResult.status]} style={{ marginLeft: 8 }}>
                                      {trackingResult.status?.toUpperCase()}
                                    </Tag>
                                  </div>
                                  <Text strong style={{ color: '#16a34a' }}>{formatMoney(trackingResult.total || 0, trackingResult.currency)}</Text>
                                </div>
                                <div className="track-result-items">
                                  {trackingResult.items?.slice(0, 3).map((item, i) => (
                                    <div key={i} className="track-result-item">
                                      <Text style={{ fontSize: 12 }}>{item.product_name}</Text>
                                      <Tag color={statusColors[item.status]} style={{ fontSize: 11 }}>{item.status}</Tag>
                                    </div>
                                  ))}
                                </div>
                                <Button type="link" size="small" style={{ padding: 0, height: 'auto', marginTop: 6 }}
                                  onClick={() => setModalVisible(true)}>
                                  View Full Details →
                                </Button>
                              </div>
                            </motion.div>
                          )}

                          {orderData && (
                            <OrderDetailModal
                              order={orderData}
                              visible={modalVisible}
                              onCancel={() => setModalVisible(false)}
                            />
                          )}
                        </div>
                      )
                    }
                  ]}
                />
                </div>
              <Text className="hero-subtitle-mini"><i>Queries: +27 815901227 | ask@tanaka.co.za</i></Text>
            </motion.div>
          </div>

          <div className="service-categories">
            <Row gutter={[16, 16]} wrap={true}>
              {providers.map((provider, index) => (
                <Col 
                  key={provider.id} 
                  style={{ 
                    flex: '1 0 200px', 
                    maxWidth: 'none', 
                    minWidth: '200px' 
                  }}
                >
                  <motion.div variants={itemVariants} whileHover={{ y: -5, transition: { duration: 0.2 } }}>
                    <Link to={`/categories?provider=${provider.id}`}>
                      <Card className="category-card" size="small" style={{ borderColor: "white", borderRadius: "15px", backgroundColor:'#f26522' }}>
                        <div className="category-icon" style={{ color: provider.color }}>
                          <img src={provider.icon} width="75px" style={{borderRadius:"40px"}} alt={provider.title} />
                        </div>
                        <Text className="category-title">{provider.title}</Text>
                      </Card>
                    </Link>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        <motion.div variants={containerVariants} initial="hidden" animate="visible">

    {showInstallPrompt && (
      // A slim, dismissible bar — not a full-page card. It used to show all
      // three platforms' instructions stacked at once whenever expanded; now
      // it shows exactly one, decided from the real device, and iOS gets its
      // two-step instructions inline since Apple gives no installable API to
      // trigger. Dismissing persists via localStorage so it does not return.
      <div className="pwa-install-bar-wrap">
        <motion.div
          className="pwa-install-bar"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          <DownloadOutlined className="pwa-install-bar-icon" />

          <div className="pwa-install-bar-text">
            <Text strong className="pwa-install-bar-title">Install Tanaka Life</Text>
            <Text type="secondary" className="pwa-install-bar-subtitle">
              {installPlatform === 'ios'
                ? 'Tap Share, then "Add to Home Screen"'
                : 'Faster access, works offline'}
            </Text>
          </div>

          {/* iOS never has a native prompt to trigger — the subtitle above
              already carries its two steps, so no button here for it. */}
          {installPlatform !== 'ios' && canPromptInstall && (
            <Button
              type="primary"
              size="small"
              onClick={handleInstallClick}
              className="pwa-install-bar-action"
            >
              Install
            </Button>
          )}

          <button
            type="button"
            className="pwa-install-bar-close"
            aria-label="Dismiss install prompt"
            onClick={handleDismissInstall}
          >
            <CloseOutlined />
          </button>
        </motion.div>
      </div>
    )}

    {/* Rest of the sections remain unchanged... */}
  </motion.div>

        {/* Features Section */}
        <section className="features-section">
          <Row gutter={[32, 32]} justify="center">
            <Col xs={24} sm={8} md={8}>
              <motion.div
                className="feature-item"
                variants={itemVariants}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="feature-icon">
                  <MobileOutlined />
                </div>
                <Title level={3}>Instant Recharging</Title>
                <Text>Get your airtime, data bundles, and utility tokens instantly delivered to your account.</Text>
              </motion.div>
            </Col>

            <Col xs={24} sm={8} md={8}>
              <motion.div
                className="feature-item"
                variants={itemVariants}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="feature-icon">
                  <DollarOutlined />
                </div>
                <Title level={3}>Competitive Rates</Title>
                <Text>Enjoy the best rates for all your digital purchases and utility payments.</Text>
              </motion.div>
            </Col>

            <Col xs={24} sm={8} md={8}>
              <motion.div
                className="feature-item"
                variants={itemVariants}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="feature-icon">
                  <CheckCircleOutlined />
                </div>
                <Title level={3}>Reliable Service</Title>
                <Text>Count on our dependable platform for all your digital needs with 24/7 customer support.</Text>
              </motion.div>
            </Col>
          </Row>
        </section>

        {/* Products Showcase */}
        <section className="products-showcase">
          <Title level={2} className="section-title">
            Popular Categories
          </Title>

          <Row gutter={[24, 24]}>

            {popularCategories.map((category, index) => (
                <Col xs={24} sm={12} md={8} lg={6} key={index}>
                <motion.div variants={itemVariants} whileHover={{ y: -5, transition: { duration: 0.2 } }}>
                  <Card
                    hoverable
                    cover={<img src={category.icon} alt={category.title} loading="lazy" />}
                    className="product-card"
                    size="small"
                  >
                    <Card.Meta title={category.title} description={category.range} />
                    {/* Navigates directly: a <button> nested inside an <a> is
                        invalid markup and gives one action two hit areas. */}
                    <div className="product-card-action">
                      <Button
                        type="primary"
                        block
                        className="product-button"
                        onClick={() => navigate(`/products?category=${category.id}`)}
                      >
                        Explore
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              </Col>
            ))}

          </Row>

          <div className="view-all-container">
            {/* Navigates directly rather than wrapping in a Link: a <button>
                inside an <a> is invalid markup and gives one action two
                overlapping hit areas. */}
            <Button type="primary" size="large" onClick={() => navigate('/categories')}>
              View All Categories
            </Button>
          </div>
        </section>

        {/* Testimonials */}
        <section className="testimonials-section">
          <Title level={2} className="section-title">
            What Our Customers Say
          </Title>

          <Carousel autoplay className="testimonial-carousel">
            <div className="testimonial-item">
              <div className="testimonial-content">
                <div className="quote">"</div>
                <Text className="testimonial-text">
                  I've been using Tanaka Life for all my ZESA token purchases and airtime top-ups. The service is fast,
                  reliable, and the rates are very competitive. Highly recommended!
                </Text>
                <div className="testimonial-author">
                  <Text strong>John M.</Text>
                  <Text type="secondary">Johannesburg</Text>
                </div>
              </div>
            </div>

            <div className="testimonial-item">
              <div className="testimonial-content">
                <div className="quote">"</div>
                <Text className="testimonial-text">
                  The wallet feature makes it so convenient to manage my funds and make quick purchases. Customer
                  service is also excellent whenever I've needed assistance.
                </Text>
                <div className="testimonial-author">
                  <Text strong>Sarah T.</Text>
                  <Text type="secondary">Cape Town</Text>
                </div>
              </div>
            </div>

            <div className="testimonial-item">
              <div className="testimonial-content">
                <div className="quote">"</div>
                <Text className="testimonial-text">
                  Being able to buy WhatsApp bundles for my family back home in Zimbabwe has been a game-changer. The
                  process is simple and the delivery is instant. Thank you Tanaka Life!
                </Text>
                <div className="testimonial-author">
                  <Text strong>Michael R.</Text>
                  <Text type="secondary">Pretoria</Text>
                </div>
              </div>
            </div>
          </Carousel>
        </section>
      </motion.div>
    </div>
  )
}

export default HomePage
